import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getMpGuide, MP_GUIDES } from "@/lib/mp-guides";
import { MP_COMPARISONS } from "@/lib/mp-compare";
import { MARKETPLACE_OG_IMAGE, MARKETPLACE_OG_IMAGE_ALT, SITE_URL, buildPageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";

export const Route = createFileRoute("/marketplace/guides/$slug")({
  loader: ({ params }) => {
    const guide = getMpGuide(params.slug);
    if (!guide) throw notFound();
    return { slug: guide.slug };
  },
  head: ({ params }) => {
    const guide = getMpGuide(params.slug);
    if (!guide) {
      return buildPageMetadata({
        path: `/marketplace/guides/${params.slug}`,
        title: "Guide not found | BeHot.Now",
        description: "This guide is not available on BeHot.Now.",
        noindex: true,
      });
    }
    return buildPageMetadata({
      path: `/marketplace/guides/${guide.slug}`,
      title: guide.title,
      description: guide.description,
      ogTitle: guide.h1,
      ogDescription: guide.description,
      image: MARKETPLACE_OG_IMAGE,
      imageAlt: MARKETPLACE_OG_IMAGE_ALT,
      keywords: guide.keywords,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Marketplace", path: "/marketplace" },
        { name: "Guides", path: "/marketplace/guides" },
        { name: guide.h1, path: `/marketplace/guides/${guide.slug}` },
      ],
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: guide.h1,
        description: guide.description,
        url: `${SITE_URL}/marketplace/guides/${guide.slug}`,
        isPartOf: { "@type": "WebSite", url: SITE_URL, name: "BeHot.Now" },
      },
    });
  },
  component: GuidePage,
});

function GuidePage() {
  const { slug } = Route.useParams();
  const { t } = useT();
  const guide = getMpGuide(slug);

  if (!guide) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold">{t("mpseo.notFound")}</h1>
        <Button asChild variant="hot" className="mt-4">
          <Link to="/marketplace/guides">{t("mpseo.allGuides")}</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          {t("mpseo.home")}
        </Link>
        <span className="px-1.5">/</span>
        <Link to="/marketplace" className="hover:text-foreground">
          {t("mpseo.marketplace")}
        </Link>
        <span className="px-1.5">/</span>
        <Link to="/marketplace/guides" className="hover:text-foreground">
          {t("mpseo.guides")}
        </Link>
      </nav>

      <header className="mt-4">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
          <BookOpen className="size-3.5" /> {t("mpseo.guides")}
        </span>
        <h1 className="mt-3 font-display text-3xl font-bold leading-tight sm:text-4xl">{guide.h1}</h1>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">{guide.intro}</p>
      </header>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button asChild variant="hot">
          <Link to="/marketplace">
            {t("mpseo.cta.open")} <ArrowRight className="ml-1 size-4" />
          </Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="/marketplace/sell">{t("mpseo.cta.sell")}</Link>
        </Button>
      </div>

      <div className="mt-10 space-y-8">
        {guide.sections.map((section, i) => (
          <section key={i}>
            <h2 className="font-display text-xl font-bold sm:text-2xl">{section.h2}</h2>
            {section.body.map((p, j) => (
              <p key={j} className="mt-3 leading-relaxed text-muted-foreground">
                {p}
              </p>
            ))}
          </section>
        ))}
      </div>

      <section className="mt-12">
        <h2 className="font-display text-xl font-bold sm:text-2xl">{t("mpseo.questions")}</h2>
        <dl className="mt-4 space-y-4">
          {guide.faq.map(([q, a], i) => (
            <div key={i} className="card-surface p-4">
              <dt className="font-semibold">{q}</dt>
              <dd className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{a}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mt-12">
        <h2 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
          {t("mpseo.keepReading")}
        </h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {guide.related.map((r) => {
            const target = getMpGuide(r);
            if (!target) return null;
            return (
              <Link
                key={r}
                to="/marketplace/guides/$slug"
                params={{ slug: r }}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
              >
                {target.h1}
              </Link>
            );
          })}
          <Link
            to="/compare/marketplace"
            className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            {t("mpseo.compareOthers")}
          </Link>
          <Link
            to="/how-it-works"
            className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            {t("mpseo.howItWorks")}
          </Link>
        </div>
      </section>

      {slug === "how-the-marketplace-works" && (
        <section className="mt-12">
          <h2 className="font-display text-xl font-bold sm:text-2xl">{t("mpseo.everyGuide")}</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {MP_GUIDES.filter((g) => g.slug !== slug).map((g) => (
              <Link
                key={g.slug}
                to="/marketplace/guides/$slug"
                params={{ slug: g.slug }}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
              >
                {g.h1}
              </Link>
            ))}
            {MP_COMPARISONS.slice(0, 3).map((c) => (
              <Link
                key={c.slug}
                to="/compare/marketplace/$competitor"
                params={{ competitor: c.slug }}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
              >
                {t("mpseo.vs", { brand: c.brand })}
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
